import React from 'react';
import Modal from '../Modal';
import { useQuery } from '@tanstack/react-query';
import { saudeService } from '../../services/saude.service';
import {
  HeartIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import type { SaudeTimelineItemContract } from '@sstexames/shared-types';

interface TimelineSaudeModalProps {
  isOpen: boolean;
  onClose: () => void;
  colaboradorId: number;
  colaboradorNome?: string;
}

export default function TimelineSaudeModal({
  isOpen,
  onClose,
  colaboradorId,
  colaboradorNome,
}: TimelineSaudeModalProps) {
  const { data: timeline = [], isLoading } = useQuery({
    queryKey: ['saude-timeline', colaboradorId],
    queryFn: () => saudeService.obterTimelineColaborador(colaboradorId),
    enabled: isOpen && !!colaboradorId,
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Prontuário Longitudinal & Linha do Tempo: ${colaboradorNome || `#${colaboradorId}`}`}
      size="xl"
    >
      <div className="space-y-6 max-h-[75vh] overflow-y-auto pr-1">
        {/* Banner Informativo */}
        <div className="bg-gradient-to-r from-teal-50 via-cyan-50 to-blue-50 border border-teal-200/80 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-teal-600 rounded-lg text-white shadow-xs">
              <HeartIcon className="w-5 h-5" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-gray-900">
                Histórico Clínico e Ocupacional Unificado
              </h4>
              <p className="text-xs text-gray-600">
                Eventos agregados de medicina do trabalho, exames complementares e atestados médicos externos.
              </p>
            </div>
          </div>
          <span className="text-xs font-bold text-teal-700 bg-teal-100 px-3 py-1 rounded-full border border-teal-200">
            {timeline.length} evento(s)
          </span>
        </div>

        {/* Timeline Loading / Empty / Content */}
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400">
            <ArrowPathIcon className="w-8 h-8 animate-spin text-teal-600 mb-2" />
            <span className="text-xs font-medium">Carregando prontuário longitudinal...</span>
          </div>
        ) : timeline.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <DocumentTextIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p className="text-sm font-semibold text-gray-600">
              Nenhum evento de saúde registrado para este colaborador.
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Os novos atestados ou exames sincronizados aparecerão aqui automaticamente.
            </p>
          </div>
        ) : (
          <div className="relative pl-6 sm:pl-8 space-y-6 before:absolute before:left-3 before:top-2 before:bottom-2 before:w-0.5 before:bg-gray-200">
            {timeline.map((item: SaudeTimelineItemContract) => {
              const isAtestado = item.tipo_evento === 'ATESTADO_MEDICO';
              const isAlerta =
                item.destaque_alerta ||
                item.parecer_ou_status?.includes('INAPTO') ||
                item.parecer_ou_status?.includes('RECUSADO');

              return (
                <div key={item.id} className="relative group">
                  {/* Ponto / Ícone da Timeline */}
                  <div
                    className={`absolute -left-6 sm:-left-8 top-1.5 w-6 h-6 rounded-full flex items-center justify-center ring-4 ring-white ${
                      isAlerta
                        ? 'bg-rose-500 text-white'
                        : isAtestado
                        ? 'bg-indigo-600 text-white'
                        : 'bg-emerald-600 text-white'
                    }`}
                  >
                    {isAlerta ? (
                      <ExclamationTriangleIcon className="w-3.5 h-3.5" />
                    ) : isAtestado ? (
                      <DocumentTextIcon className="w-3.5 h-3.5" />
                    ) : (
                      <ShieldCheckIcon className="w-3.5 h-3.5" />
                    )}
                  </div>

                  {/* Card do Evento */}
                  <div
                    className={`p-4 rounded-xl border transition shadow-xs ${
                      isAlerta
                        ? 'bg-rose-50/50 border-rose-200 hover:border-rose-300'
                        : isAtestado
                        ? 'bg-white border-gray-200/90 hover:border-indigo-300'
                        : 'bg-white border-gray-200/90 hover:border-emerald-300'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span
                            className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                              isAtestado
                                ? 'bg-indigo-100 text-indigo-700'
                                : 'bg-emerald-100 text-emerald-800'
                            }`}
                          >
                            {isAtestado ? 'Atestado Médico' : 'Exame Ocupacional'}
                          </span>
                          <h5 className="text-sm font-bold text-gray-900">
                            {item.titulo}
                          </h5>
                        </div>
                        {item.subtitulo && (
                          <p className="text-xs text-gray-500 mt-0.5">
                            {item.subtitulo}
                          </p>
                        )}
                      </div>

                      {/* Parecer / Status */}
                      <span
                        className={`text-xs font-semibold px-2.5 py-1 rounded-md border ${
                          isAlerta
                            ? 'bg-rose-100 text-rose-800 border-rose-200'
                            : 'bg-emerald-50 text-emerald-800 border-emerald-200'
                        }`}
                      >
                        {item.parecer_ou_status}
                      </span>
                    </div>

                    {/* Detalhes Médicos & Patológicos */}
                    <div className="mt-3 pt-3 border-t border-gray-100 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs text-gray-600">
                      <div>
                        <span className="font-semibold text-gray-700">Data:</span>{' '}
                        {new Date(item.data_evento).toLocaleDateString('pt-BR')}
                      </div>

                      {item.dias_afastamento > 0 && (
                        <div>
                          <span className="font-semibold text-gray-700">
                            Afastamento:
                          </span>{' '}
                          <span className="font-bold text-amber-700">
                            {item.dias_afastamento} dia(s)
                          </span>
                        </div>
                      )}

                      {item.cid_codigo && (
                        <div>
                          <span className="font-semibold text-gray-700">CID-10:</span>{' '}
                          <span className="font-bold text-indigo-700">
                            {item.cid_codigo}
                          </span>{' '}
                          {item.cid_capitulo ? (
                            <span className="text-[11px] text-gray-400">
                              ({item.cid_capitulo})
                            </span>
                          ) : null}
                        </div>
                      )}

                      {item.medico_ou_clinica && (
                        <div className="sm:col-span-2">
                          <span className="font-semibold text-gray-700">
                            Médico / Estabelecimento:
                          </span>{' '}
                          {item.medico_ou_clinica}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex justify-end pt-4 border-t border-gray-200 mt-6">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold text-xs rounded-lg transition"
        >
          Fechar
        </button>
      </div>
    </Modal>
  );
}
